import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, Invoice } from '@nexus/shared/types';
import { toast } from './toastStore';

interface FinanceState {
  invoices: Invoice[];
  total: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  create: (data: Record<string, unknown>) => Promise<Invoice>;
  update: (id: string, data: Record<string, unknown>) => Promise<void>;
  markPaid: (id: string) => Promise<void>;
  followUp: (id: string) => Promise<void>;
  bulkUpdate: (ids: string[], updates: Record<string, unknown>) => Promise<void>;
  bulkFollowUp: (ids: string[]) => Promise<void>;
  clearError: () => void;
}

function convert(i: Record<string, unknown>): Invoice {
  return {
    ...i,
    createdAt: fromUnix(i.createdAt as number),
    updatedAt: fromUnix(i.updatedAt as number),
    dueDate: fromUnix(i.dueDate as number),
    paidDate: i.paidDate ? fromUnix(i.paidDate as number) : undefined,
    lastFollowUpAt: i.lastFollowUpAt ? fromUnix(i.lastFollowUpAt as number) : undefined,
    lineItems: typeof i.lineItems === 'string' ? JSON.parse(i.lineItems as string) : i.lineItems,
  } as Invoice;
}

export const useFinanceStore = create<FinanceState>((set, get) => ({
  invoices: [],
  total: 0,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<Invoice[]>>(`/api/finance/invoices${toQueryString(params)}`);
      set({
        invoices: (res.data ?? []).map(i => convert(i as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  create: async (data) => {
    try {
      const res = await api.post<ApiResponse<Invoice>>('/api/finance/invoices', data);
      const invoice = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ invoices: [invoice, ...s.invoices], total: s.total + 1 }));
      toast.success('Invoice created');
      return invoice;
    } catch (err) {
      toast.error('Failed to create invoice', (err as Error).message);
      throw err;
    }
  },

  update: async (id, data) => {
    try {
      const res = await api.patch<ApiResponse<Invoice>>(`/api/finance/invoices/${id}`, data);
      const invoice = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ invoices: s.invoices.map((i) => (i.id === id ? invoice : i)) }));
      toast.success('Invoice updated');
    } catch (err) {
      toast.error('Failed to update invoice', (err as Error).message);
      throw err;
    }
  },

  markPaid: async (id) => {
    try {
      const res = await api.post<ApiResponse<Invoice>>(`/api/finance/invoices/${id}/paid`);
      const invoice = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ invoices: s.invoices.map((i) => (i.id === id ? invoice : i)) }));
      toast.success('Invoice marked as paid');
    } catch (err) {
      toast.error('Failed to mark invoice as paid', (err as Error).message);
      throw err;
    }
  },

  followUp: async (id) => {
    try {
      const res = await api.post<ApiResponse<Invoice>>(`/api/finance/invoices/${id}/follow-up`);
      const invoice = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ invoices: s.invoices.map((i) => (i.id === id ? invoice : i)) }));
      toast.success('Follow-up sent');
    } catch (err) {
      toast.error('Failed to send follow-up', (err as Error).message);
      throw err;
    }
  },

  bulkUpdate: async (ids, updates) => {
    try {
      await api.patch('/api/finance/invoices/bulk', { ids, updates });
      toast.success(`Updated ${ids.length} invoice${ids.length !== 1 ? 's' : ''}`);
      get().fetch();
    } catch (err) {
      toast.error('Bulk update failed', (err as Error).message);
      throw err;
    }
  },

  bulkFollowUp: async (ids) => {
    try {
      await api.post('/api/finance/invoices/bulk/follow-up', { ids });
      toast.success(`Sent follow-ups for ${ids.length} invoice${ids.length !== 1 ? 's' : ''}`);
      get().fetch();
    } catch (err) {
      toast.error('Bulk follow-up failed', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
